<script setup>
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import IconPicker from '../../../../components/ui-components/IconPicker.vue'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'
import InputNumber from 'primevue/inputnumber'
import Select from 'primevue/select'
import Checkbox from 'primevue/checkbox'
import Popover from 'primevue/popover'
import ColorPicker from 'primevue/colorpicker'
import CalendarSelect from '../../common/CalendarSelect.vue'
import TimeSlotPicker from '../../personal/TimeSlotPicker.vue'
import Editor from 'primevue/editor'
import RentalItemCard from '../../../../components/ui-components/widget/RentalItemCard.vue'
import { trans } from 'laravel-vue-i18n'

const visible = ref(false)
const currentSection = ref('info')
const showTimeslotPicker = ref(false)
const draggedIndex = ref(null)
const emit = defineEmits(['save'])

const model = defineModel()
const formModel = defineModel('form')

watch(() => model.value, (now) => {
    visible.value = !!now
    if (now) {
        currentSection.value = 'info'
        errors.value = {}
    }
})

watch(
    [() => model.value?.max_adults, () => model.value?.max_children],
    ([adults, children]) => {
        if (!model.value) return
        const sum = (adults ?? 0) + (children ?? 0)
        if (sum > 0 && (model.value.max_pop ?? 0) < sum) {
            model.value.max_pop = sum
        }
    }
)

const sections = [
    { id: 'info',         icon: 'pi pi-tag',        label: trans('rental.section_info') },
    { id: 'gallery',      icon: 'pi pi-images',      label: trans('rental.section_gallery') },
    { id: 'attributes',   icon: 'pi pi-list',        label: trans('rental.section_attributes') },
    { id: 'price',        icon: 'pi pi-dollar',      label: trans('rental.section_price') },
    { id: 'availability', icon: 'pi pi-calendar',    label: trans('rental.section_availability') },
    { id: 'extras',       icon: 'pi pi-plus-circle', label: trans('rental.section_extras') },
    { id: 'advanced',     icon: 'pi pi-sliders-h',   label: trans('rental.section_advanced') },
]

const errors = ref({})

const sectionStatus = computed(() => {
    if (!model.value) return {}
    return {
        info: model.value.name?.trim() ? 'done' : (errors.value.name ? 'error' : 'empty'),
        gallery: (model.value.gallery?.length ?? 0) > 0 ? 'done' : 'empty',
        attributes: (model.value.attributes?.length ?? 0) > 0 ? 'done' : 'empty',
        price: model.value.unit_price > 0 ? 'done' : 'empty',
        availability: model.value.calendar_id ? 'done' : (errors.value.calendar_id ? 'error' : 'empty'),
        extras: ((model.value.additional_costs?.length ?? 0) + (model.value.discounts?.length ?? 0)) > 0 ? 'done' : 'empty',
        advanced: ((model.value.pop_rules?.length ?? 0) + (model.value.occupancy_rules?.length ?? 0)) > 0 ? 'done' : 'empty',
    }
})

const previewItem = computed(() => {
    if (!model.value) return null
    const gallery = (model.value.gallery ?? []).map(img =>
        typeof img === 'string' ? { full_url: img } : img
    )
    return {
        ...model.value,
        gallery,
        prices: [{
            unit_price: model.value.unit_price,
            currency: model.value.currency,
            vat_percentage: model.value.vat_percentage,
            note: '',
        }],
    }
})

const currencies = ref([
    { code: 'CZK', label: 'Kč' },
    { code: 'EUR', label: '€' },
])

const unitOptions = computed(() => {
    if (formModel.value.reservationType === 'multi-day') {
        return [
            { label: trans('rental.unit_day'),     value: 'day',     and_more: trans('rental.unit_day_more') },
            { label: trans('rental.unit_weekend'),  value: 'weekend', and_more: trans('rental.unit_weekend_more') },
            { label: trans('rental.unit_week'),     value: 'week',    and_more: trans('rental.unit_week_more') },
            { label: trans('rental.unit_month'),    value: 'month',   and_more: trans('rental.unit_month_more') },
        ]
    }
    return [
        { label: trans('rental.unit_hour'),   value: 'hour',   and_more: trans('rental.unit_hour_more') },
        { label: trans('rental.unit_minute'), value: 'minute', and_more: trans('rental.unit_minute_more') },
    ]
})

const paymentStyles = [
    { label: trans('rental.payment_before'), value: 'before' },
    { label: trans('rental.payment_after'),  value: 'after' },
]

const blockingTypes = [
    { label: trans('rental.blocking_start'),     value: 'start' },
    { label: trans('rental.blocking_end'),       value: 'end' },
    { label: trans('rental.blocking_intersect'), value: 'intersect' },
]

const days = [
    { label: trans('common.dow_long.mon'), value: 'monday' },
    { label: trans('common.dow_long.tue'), value: 'tuesday' },
    { label: trans('common.dow_long.wed'), value: 'wednesday' },
    { label: trans('common.dow_long.thu'), value: 'thursday' },
    { label: trans('common.dow_long.fri'), value: 'friday' },
    { label: trans('common.dow_long.sat'), value: 'saturday' },
    { label: trans('common.dow_long.sun'), value: 'sunday' },
]

const discountStyles = [
    { value: 'only_from_rental',         label: trans('rental.discount_only_rental') },
    { value: 'rental_and_additional',    label: trans('rental.discount_rental_and_costs') },
]

const occupancyTimeOptions = [
    { label: trans('rental.occ_time_week'),  value: 'week' },
    { label: trans('rental.occ_time_month'), value: 'month' },
    { label: trans('rental.occ_time_year'),  value: 'year' },
]

const modifierTypes = [
    { label: '%',                       value: 'percentage' },
    { label: trans('rental.modifier_fixed').trim(), value: 'fixed' },
]

function addAttribute() {
    model.value?.attributes.push({
        icon: 'pi pi-star',
        label: '',
        value: '',
        value_type: 'string',
        is_highlighted: false,
        is_visible: true,
        color: '#000000',
        note: '',
    })
}

function removeAttribute(index) {
    model.value?.attributes.splice(index, 1)
}

function addDiscount() {
    model.value?.discounts.push({ min_units: 2, percentage_off: 10, discount_style: 'only_from_rental' })
}

function removeDiscount(index) {
    model.value?.discounts.splice(index, 1)
}

function addAdditionalCost() {
    model.value?.additional_costs.push({
        name: '',
        description: '',
        unit_price: 0,
        vat_percentage: 21,
        currency: 'CZK',
        is_optional: false,
    })
}

function removeAdditionalCost(index) {
    model.value?.additional_costs.splice(index, 1)
}

function addBookingRule() {
    model.value?.booking_rules.push({ day: '', blocking_type: '' })
}

function removeBookingRule(index) {
    model.value.booking_rules.splice(index, 1)
}

function addPopRule() {
    if (!model.value.pop_rules) model.value.pop_rules = []
    const last = model.value.pop_rules.at(-1)?.bounds_upper ?? 1
    model.value.pop_rules.push({ bounds_lower: last, bounds_upper: null, price_multiplier: 1, price_rebaser: null })
}

function removePopRule(index) {
    model.value.pop_rules.splice(index, 1)
}

function addOccupancyRule() {
    model.value.occupancy_rules.push({
        occupancy_threshold_percentage: 50,
        occupancy_collection_time: 'week',
        price_modifier_type: 'percentage',
        price_modifier_value: 10,
        show_price_change: true,
    })
}

function removeOccupancyRule(index) {
    model.value.occupancy_rules.splice(index, 1)
}

function onGalleryUpload(event) {
    const files = event.target.files
    if (!files.length) return
    Array.from(files).forEach((file) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            model.value?.gallery.push(e.target.result)
        }
        reader.readAsDataURL(file)
    })
    event.target.value = ''
}

function onDropImage(targetIndex, sourceIndex) {
    if (targetIndex === sourceIndex) return
    const reordered = [...model.value.gallery]
    const [moved] = reordered.splice(sourceIndex, 1)
    reordered.splice(targetIndex, 0, moved)
    model.value.gallery = reordered
}

const currentSectionIndex = computed(() => sections.findIndex(s => s.id === currentSection.value))
const isLastSection = computed(() => currentSectionIndex.value === sections.length - 1)

function goToNextSection() {
    if (currentSectionIndex.value < sections.length - 1) {
        currentSection.value = sections[currentSectionIndex.value + 1].id
    }
}

function save() {
    errors.value = {}
    let valid = true
    if (!model.value.calendar_id) {
        errors.value.calendar_id = trans('rental.error_calendar_required')
        currentSection.value = 'availability'
        valid = false
    }
    if (!model.value.name?.trim()) {
        errors.value.name = trans('rental.error_name_required')
        currentSection.value = 'info'
        valid = false
    }
    if (valid) emit('save')
}
</script>

<template>
    <Dialog v-model:visible="visible" modal
            class="xl:min-w-[1300px] w-[96vw] overflow-hidden !p-0" position="center">
        <template #container>
            <div v-if="model" class="flex flex-col max-h-[90vh] overflow-hidden rounded-xl bg-white dark:bg-neutral-950">

                <!-- Header -->
                <div class="flex items-center justify-between px-6 py-4 border-b dark:border-neutral-700 shrink-0">
                    <div class="flex items-center gap-3">
                        <i v-if="model.icon" :class="model.icon" class="text-roori-blue text-xl shrink-0"></i>
                        <h2 class="font-bold text-lg text-gray-900 dark:text-white truncate">
                            {{ model.name?.trim() || $t('rental.new_item_title') }}
                        </h2>
                    </div>
                    <Button icon="pi pi-times" text rounded severity="secondary" @click="model = false"/>
                </div>

                <!-- Mobile tab bar -->
                <div class="md:hidden overflow-x-auto shrink-0 border-b dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900">
                    <div class="flex gap-1 p-2 w-max">
                        <button
                            v-for="section in sections"
                            :key="section.id"
                            class="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors relative"
                            :class="currentSection === section.id
                                ? 'bg-roori-blue text-white'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800'"
                            @click="currentSection = section.id"
                        >
                            <i :class="section.icon" class="text-xs"></i>
                            {{ section.label }}
                            <span v-if="sectionStatus[section.id] === 'error'"
                                  class="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-500"></span>
                        </button>
                    </div>
                </div>

                <!-- Body: three panels -->
                <div class="flex grow overflow-hidden min-h-0">

                    <!-- Left sidebar (md+) -->
                    <div class="hidden md:flex md:flex-col w-[190px] border-r dark:border-neutral-700 shrink-0 overflow-y-auto bg-neutral-50 dark:bg-neutral-900 p-3 gap-0.5">
                        <button
                            v-for="section in sections"
                            :key="section.id"
                            class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-left w-full group"
                            :class="currentSection === section.id
                                ? 'bg-white dark:bg-neutral-800 font-semibold shadow-sm text-gray-900 dark:text-white'
                                : 'text-gray-600 dark:text-gray-400 hover:bg-white/70 dark:hover:bg-neutral-800/70 hover:text-gray-900 dark:hover:text-white'"
                            @click="currentSection = section.id"
                        >
                            <i :class="[section.icon, currentSection === section.id ? 'text-roori-blue' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300']"
                               class="w-4 shrink-0 text-center"></i>
                            <span class="grow truncate">{{ section.label }}</span>
                            <span class="w-2 h-2 rounded-full shrink-0 transition-colors"
                                  :class="{
                                      'bg-green-500': sectionStatus[section.id] === 'done',
                                      'bg-red-500 animate-pulse': sectionStatus[section.id] === 'error',
                                      'bg-gray-200 dark:bg-neutral-700': sectionStatus[section.id] === 'empty',
                                  }"></span>
                        </button>
                    </div>

                    <!-- Center: form -->
                    <div class="grow overflow-y-auto min-w-0 bg-white dark:bg-neutral-950">

                        <!-- Info -->
                        <div v-if="currentSection === 'info'" class="p-6 flex flex-col gap-5 max-w-2xl">
                            <div class="flex items-start gap-3">
                                <div class="shrink-0 mt-1">
                                    <icon-picker v-model="model.icon"/>
                                </div>
                                <div class="w-full">
                                    <label class="text-sm font-semibold mb-1 block">
                                        {{ $t('rental.field_name_label') }} <span class="text-red-500">*</span>
                                    </label>
                                    <InputText v-model="model.name" :placeholder="$t('rental.field_name_placeholder')"
                                               :invalid="!!errors.name" fluid size="large"
                                               @input="errors.name = null"/>
                                    <span v-if="errors.name" class="text-red-500 text-sm mt-1 block">{{ errors.name }}</span>
                                </div>
                            </div>
                            <div>
                                <label class="text-sm font-semibold mb-1 block">{{ $t('rental.field_description_label') }}</label>
                                <Editor v-model="model.description" :placeholder="$t('rental.field_description_placeholder')" editor-style="min-height: 180px"/>
                            </div>
                        </div>

                        <!-- Gallery -->
                        <div v-else-if="currentSection === 'gallery'" class="p-6">
                            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">{{ $t('rental.gallery_hint') }}</p>
                            <div class="grid grid-cols-4 gap-3 max-w-2xl" style="grid-template-rows: 140px 140px">
                                <!-- Main image (spans 2 rows, 2 cols) -->
                                <div class="col-span-2 row-span-2 dark:bg-neutral-800 bg-neutral-100 rounded-xl overflow-hidden relative flex items-center justify-center group"
                                     @drop.prevent="onDropImage(0, draggedIndex)" @dragover.prevent>
                                    <img v-if="model.gallery?.length > 0"
                                         :src="model.gallery[0]?.full_url ?? model.gallery[0]"
                                         class="w-full h-full object-cover"
                                         draggable="true"
                                         @dragstart="draggedIndex = 0"/>
                                    <label v-else class="text-sm text-gray-400 text-center flex-col gap-2 flex items-center justify-center px-2 cursor-pointer w-full h-full">
                                        <i class="pi pi-images !text-4xl opacity-40"></i>
                                        <p>{{ $t('rental.gallery_main_label') }}</p>
                                        <p class="text-xs opacity-60">{{ $t('rental.gallery_click_hint') }}</p>
                                        <input type="file" class="opacity-0 absolute" accept="image/*" @change="onGalleryUpload"/>
                                    </label>
                                    <label v-if="model.gallery?.length > 0"
                                           class="absolute right-2 bottom-2 bg-white dark:bg-neutral-800 shadow p-2 rounded-full cursor-pointer opacity-0 group-hover:opacity-100 transition">
                                        <i class="pi pi-pencil text-sm"/>
                                        <input type="file" class="hidden" accept="image/*" @change="onGalleryUpload"/>
                                    </label>
                                </div>
                                <!-- Thumbnails -->
                                <div v-for="(image, index) in model.gallery?.slice(1)" :key="index + 1"
                                     class="rounded-xl overflow-hidden border cursor-move dark:border-neutral-700 relative group"
                                     draggable="true"
                                     @dragstart="draggedIndex = index + 1"
                                     @drop.prevent="onDropImage(index + 1, draggedIndex)"
                                     @dragover.prevent>
                                    <img :src="image?.full_url ?? image" class="w-full h-full object-cover"/>
                                    <button class="absolute top-1 right-1 bg-white dark:bg-black/80 rounded-full p-1 text-xs group-hover:opacity-100 opacity-0 transition"
                                            @click="model.gallery.splice(index + 1, 1)">
                                        <i class="pi pi-times"/>
                                    </button>
                                </div>
                                <!-- Add button -->
                                <label class="border border-dashed flex flex-col items-center justify-center text-gray-400 cursor-pointer rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-800 gap-2 transition-colors">
                                    <i class="pi pi-plus text-xl"></i>
                                    <span class="text-xs">{{ $t('common.add') }}</span>
                                    <input type="file" class="hidden" accept="image/*" @change="onGalleryUpload"/>
                                </label>
                            </div>
                        </div>

                        <!-- Attributes -->
                        <div v-else-if="currentSection === 'attributes'" class="p-6 flex flex-col gap-4 max-w-2xl">
                            <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('rental.attr_hint') }}</p>
                            <div v-if="model.attributes?.length > 0" class="flex flex-col gap-2">
                                <div v-for="(attr, index) in model.attributes" :key="index"
                                     class="flex gap-2 items-center w-full p-3 rounded-xl border dark:border-neutral-700 transition-all"
                                     :class="[{ 'opacity-50': !attr.is_visible, 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800': attr.is_highlighted }]"
                                     :style="{ color: attr.color }">
                                    <icon-picker class="shrink-0" v-model="attr.icon"/>
                                    <InputText fluid v-model="attr.label" :placeholder="$t('rental.attr_name_placeholder')"/>
                                    <div class="grow" :class="{ 'mb-5': !!errors[attr.label] }">
                                        <InputText fluid v-model="attr.value" :invalid="!!errors[attr.label]" :placeholder="$t('rental.attr_value_placeholder')"/>
                                        <span v-if="errors[attr.label]" class="text-red-500 text-xs mt-0.5 block">{{ errors[attr.label] }}</span>
                                    </div>
                                    <Button text icon="pi pi-cog" severity="secondary" rounded size="small"
                                            @click="attr.panel.toggle($event)"/>
                                    <Popover :ref="el => attr.panel = el">
                                        <div class="flex flex-col gap-3 min-w-[220px] px-1">
                                            <div class="flex items-center gap-2">
                                                <i class="pi pi-eye"/>
                                                <Checkbox v-model="attr.is_visible" binary/>
                                                <label>{{ $t('rental.attr_show_label') }}</label>
                                            </div>
                                            <div class="flex items-center gap-2">
                                                <i class="pi pi-star-fill text-yellow-500"/>
                                                <Checkbox v-model="attr.is_highlighted" binary/>
                                                <label>{{ $t('rental.attr_highlight_label') }}</label>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <label class="text-sm font-semibold">{{ $t('rental.attr_color_label') }}</label>
                                                <ColorPicker v-model="attr.color" inline format="hex"/>
                                            </div>
                                        </div>
                                    </Popover>
                                    <Button text icon="pi pi-trash" severity="danger" rounded size="small"
                                            @click="removeAttribute(index)"/>
                                </div>
                            </div>
                            <p v-else class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.attr_empty') }}</p>
                            <Button size="small" :label="$t('rental.attr_add_btn')" icon="pi pi-plus" outlined class="self-start" @click="addAttribute"/>
                        </div>

                        <!-- Price -->
                        <div v-else-if="currentSection === 'price'" class="p-6 flex flex-col gap-6 max-w-2xl">
                            <div class="flex flex-col gap-4">
                                <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{{ $t('rental.price_basic') }}</p>
                                <div class="flex gap-3 flex-wrap">
                                    <div class="flex flex-col gap-1 grow min-w-[140px]">
                                        <label class="text-sm font-semibold">{{ $t('rental.price_without_vat') }}</label>
                                        <InputNumber v-model="model.unit_price" mode="currency" size="large"
                                                     :currency="model.currency" currencyDisplay="symbol" locale="cs-CZ"
                                                     fluid :invalid="!!errors.unit_price"/>
                                    </div>
                                    <div class="flex flex-col gap-1 min-w-[80px]">
                                        <label class="text-sm font-semibold">{{ $t('rental.price_currency') }}</label>
                                        <Select variant="filled" :options="currencies" option-label="label"
                                                option-value="code" size="large" fluid v-model="model.currency"/>
                                    </div>
                                    <div class="flex flex-col gap-1 grow min-w-[120px]">
                                        <label class="text-sm font-semibold">{{ $t('rental.price_unit') }}</label>
                                        <Select v-model="model.unit" size="large" :options="unitOptions"
                                                option-value="value" option-label="label" fluid/>
                                    </div>
                                    <div class="flex flex-col gap-1 min-w-[70px]">
                                        <label class="text-sm font-semibold">{{ $t('rental.price_vat') }}</label>
                                        <InputNumber fluid v-model="model.vat_percentage" show-buttons :min="0" :max="100"/>
                                    </div>
                                </div>
                                <div class="flex gap-3 flex-wrap">
                                    <div v-if="formModel.reservationType === 'multi-day'" class="flex flex-col gap-1">
                                        <label class="text-sm font-semibold">{{ $t('rental.price_min_label') }}</label>
                                        <InputNumber fluid show-buttons
                                                     :suffix="` ${unitOptions.find(el => el.value === model.unit)?.label.toLowerCase()}`"
                                                     v-model="model.min_unit_quantity"/>
                                    </div>
                                    <div class="flex flex-col gap-1 grow">
                                        <label class="text-sm font-semibold">{{ $t('rental.price_payment') }}</label>
                                        <Select v-model="model.payment_style" :options="paymentStyles"
                                                option-label="label" option-value="value"/>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Availability -->
                        <div v-else-if="currentSection === 'availability'" class="p-6 flex flex-col gap-6 max-w-2xl">
                            <div class="flex flex-col gap-2">
                                <label class="text-sm font-semibold">{{ $t('rental.avail_calendar_label') }} <span class="text-red-500">*</span></label>
                                <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('rental.avail_calendar_hint') }}</p>
                                <CalendarSelect v-model:form="formModel" :invalid="!!errors.calendar_id"
                                                v-model="model.calendar_id" @update:model-value="errors.calendar_id = null"/>
                                <span v-if="errors.calendar_id" class="text-red-500 text-sm">{{ errors.calendar_id }}</span>
                            </div>

                            <template v-if="formModel.reservationType === 'multi-day'">
                                <div class="border-t dark:border-neutral-700 pt-5 flex flex-col gap-4">
                                    <div class="flex items-start justify-between gap-4">
                                        <div>
                                            <p class="font-semibold text-sm">{{ $t('rental.avail_rules_title') }}</p>
                                            <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ $t('rental.avail_rules_hint') }}</p>
                                        </div>
                                        <Button size="small" :label="$t('common.add')" icon="pi pi-plus" outlined @click="addBookingRule"/>
                                    </div>
                                    <div v-if="model.booking_rules?.length > 0" class="flex flex-col gap-3">
                                        <div v-for="(rule, index) in model.booking_rules" :key="'obc-' + index"
                                             class="flex gap-2 flex-wrap items-end p-4 rounded-xl border dark:border-neutral-700">
                                            <div class="flex flex-col gap-1 relative">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.avail_dow_label') }}</label>
                                                <Select v-model="rule.day"
                                                        :invalid="rule.day !== '' && model.booking_rules.filter(b => b.day === rule.day).length > 1"
                                                        :options="days" option-label="label" option-value="value"/>
                                                <p v-if="rule.day !== '' && model.booking_rules.filter(b => b.day === rule.day).length > 1"
                                                   class="text-xs bg-red-500 p-0.5 px-2 rounded text-white absolute -bottom-6">
                                                    {{ $t('rental.avail_already_defined') }}</p>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.avail_blocking_label') }}</label>
                                                <Select v-model="rule.blocking_type" :options="blockingTypes"
                                                        option-label="label" option-value="value"/>
                                            </div>
                                            <Button icon="pi pi-trash" text severity="danger" @click="removeBookingRule(index)"/>
                                        </div>
                                    </div>
                                    <p v-else class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.avail_rules_empty') }}</p>
                                </div>
                            </template>

                            <template v-else>
                                <div class="border-t dark:border-neutral-700 pt-5 flex flex-col gap-3">
                                    <p class="font-semibold text-sm">{{ $t('rental.avail_section_title') }}</p>
                                    <p class="text-sm text-gray-500 dark:text-gray-400">{{ $t('rental.avail_section_hint') }}</p>
                                    <Button icon="pi pi-clock" :label="$t('rental.avail_open_btn')" outlined class="self-start" @click="showTimeslotPicker = true"/>
                                    <Dialog v-model:visible="showTimeslotPicker" modal class="w-[1200px]">
                                        <template #container>
                                            <div class="max-md:overflow-y-scroll">
                                                <div class="md:hidden p-4 border-b bg-neutral-50 rounded-t-lg flex justify-between items-center">
                                                    <p class="text-xl">{{ $t('rental.avail_section_title') }}</p>
                                                    <Button icon="pi pi-times" rounded severity="secondary" @click="showTimeslotPicker = false"/>
                                                </div>
                                                <div class="max-md:p-4">
                                                    <time-slot-picker v-model="model" class="mt-0">
                                                        <template #buttons>
                                                            <Button icon="pi pi-times" rounded severity="secondary" @click="showTimeslotPicker = false"/>
                                                        </template>
                                                    </time-slot-picker>
                                                </div>
                                            </div>
                                        </template>
                                    </Dialog>
                                </div>
                            </template>
                        </div>

                        <!-- Extras: additional costs + discounts -->
                        <div v-else-if="currentSection === 'extras'" class="p-6 flex flex-col gap-8 max-w-2xl">
                            <!-- Additional costs -->
                            <div class="flex flex-col gap-4">
                                <div class="flex items-start justify-between gap-4">
                                    <div>
                                        <p class="font-semibold">{{ $t('rental.costs_title') }}</p>
                                        <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ $t('rental.costs_hint') }}</p>
                                    </div>
                                    <Button size="small" :label="$t('common.add')" icon="pi pi-plus" outlined @click="addAdditionalCost"/>
                                </div>
                                <div v-if="model.additional_costs?.length > 0" class="flex flex-col gap-3">
                                    <div v-for="(cost, index) in model.additional_costs" :key="'cost-' + index"
                                         class="flex flex-col gap-3 p-4 rounded-xl border dark:border-neutral-700">
                                        <div class="flex gap-2 items-center flex-wrap">
                                            <InputText v-model="cost.name" :placeholder="$t('rental.cost_name_placeholder')" class="grow"/>
                                            <div class="flex items-center gap-2">
                                                <Checkbox binary v-model="cost.is_optional"/>
                                                <label class="text-sm">{{ $t('rental.cost_optional_label') }}</label>
                                            </div>
                                            <Button icon="pi pi-trash" text severity="danger" rounded @click="removeAdditionalCost(index)"/>
                                        </div>
                                        <Textarea v-model="cost.description" :placeholder="$t('rental.cost_desc_placeholder')" rows="2"/>
                                        <div class="flex gap-2 flex-wrap">
                                            <div class="flex flex-col gap-1 grow">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.price_without_vat') }}</label>
                                                <InputNumber v-model="cost.unit_price" mode="currency" :currency="model.currency"
                                                             currencyDisplay="symbol" locale="cs-CZ" fluid/>
                                            </div>
                                            <div class="flex flex-col gap-1 min-w-[90px]">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.price_vat') }}</label>
                                                <InputNumber fluid v-model="cost.vat_percentage" :min="0" :max="100"/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p v-else class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.costs_empty') }}</p>
                            </div>

                            <!-- Discounts -->
                            <div class="border-t dark:border-neutral-700 pt-6 flex flex-col gap-4">
                                <div class="flex items-start justify-between gap-4">
                                    <div>
                                        <p class="font-semibold">{{ $t('rental.discounts_title') }}</p>
                                        <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ $t('rental.discounts_hint') }}</p>
                                    </div>
                                    <Button size="small" :label="$t('common.add')" icon="pi pi-plus" outlined @click="addDiscount"/>
                                </div>
                                <div v-if="model.discounts?.length > 0" class="flex flex-col gap-3">
                                    <div v-for="(discount, index) in model.discounts" :key="'discount-' + index"
                                         class="flex flex-col gap-2 p-4 rounded-xl border dark:border-neutral-700">
                                        <div class="flex gap-2 items-center flex-wrap">
                                            <p class="text-sm text-neutral-500">{{ $t('rental.discount_at') }}</p>
                                            <InputNumber v-model="discount.min_units" fluid
                                                         :suffix="` ${unitOptions.find(el => el.value === model.unit)?.and_more ?? $t('rental.unit_and_more_default')}`"
                                                         :invalid="!discount.min_units"
                                                         @blur="() => { if (!discount.min_units) discount.min_units = 1 }"/>
                                            <p class="text-sm text-nowrap text-neutral-500">{{ $t('rental.discount_is') }}</p>
                                            <InputNumber v-model="discount.percentage_off" show-buttons suffix="%" fluid
                                                         :invalid="!discount.percentage_off"
                                                         @blur="() => { if (discount.percentage_off == null) discount.percentage_off = 10 }"/>
                                            <Button icon="pi pi-trash" text severity="danger" rounded @click="removeDiscount(index)"/>
                                        </div>
                                        <Select v-model="discount.discount_style" :options="discountStyles"
                                                option-value="value" option-label="label"/>
                                        <p class="text-xs text-neutral-400">
                                            {{ $t('rental.discount_summary', {
                                                units: discount.min_units,
                                                more: unitOptions.find(el => el.value === model.unit)?.and_more ?? $t('rental.unit_and_more_default'),
                                                off: discount.percentage_off,
                                                from: discount.discount_style === 'only_from_rental' ? $t('rental.discount_from_rental') : $t('rental.discount_from_rental_and_costs')
                                            }) }}
                                        </p>
                                    </div>
                                </div>
                                <p v-else class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.discounts_empty') }}</p>
                            </div>
                        </div>

                        <!-- Advanced: pop rules + occupancy -->
                        <div v-else-if="currentSection === 'advanced'" class="p-6 flex flex-col gap-8 max-w-2xl">
                            <!-- Pop rules (jen ubytování — u rentalu nedává smysl cenit podle počtu osob) -->
                            <div v-if="formModel.selectedType !== 'rental'" class="flex flex-col gap-4">
                                <div class="flex items-start justify-between gap-4">
                                    <div>
                                        <p class="font-semibold">{{ $t('rental.pop_title') }}</p>
                                        <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ $t('rental.pop_hint') }}</p>
                                    </div>
                                    <Button size="small" :label="$t('common.add')" icon="pi pi-plus" outlined
                                            :disabled="model.pop_rules?.at(-1)?.bounds_upper === model.max_pop"
                                            @click="addPopRule"/>
                                </div>
                                <div class="flex flex-col gap-4">
                                    <div class="flex flex-col gap-1 self-start">
                                        <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.pop_max_total') }}</label>
                                        <InputNumber v-model="model.max_pop" show-buttons :min="(model.max_adults ?? 0) + (model.max_children ?? 0) || 1" class="w-40"/>
                                        <p class="text-xs text-neutral-400">{{ $t('rental.pop_adults_children') }}</p>
                                    </div>
                                    <div class="flex gap-4 flex-wrap">
                                        <div class="flex flex-col gap-1">
                                            <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.pop_max_adults') }}</label>
                                            <InputNumber v-model="model.max_adults" show-buttons :min="1" :max="model.max_pop ?? undefined" class="" :placeholder="$t('rental.pop_no_limit')"/>
                                        </div>
                                        <div class="flex flex-col gap-1">
                                            <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.pop_max_children') }}</label>
                                            <InputNumber v-model="model.max_children" show-buttons :min="0" :max="model.max_pop ?? undefined" class="" :placeholder="$t('rental.pop_no_limit')"/>
                                        </div>
                                        <div class="flex flex-col gap-1">
                                            <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.pop_max_pets') }}</label>
                                            <InputNumber v-model="model.max_pets" show-buttons :min="0" class="" :placeholder="$t('rental.pop_pets_disabled')"/>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="model.pop_rules?.length > 0" class="flex flex-col gap-3">
                                    <div v-for="(rule, index) in model.pop_rules" :key="'pop-' + index"
                                         class="flex flex-col gap-2 p-4 rounded-xl border dark:border-neutral-700">
                                        <div class="flex gap-2 flex-wrap items-end">
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.pop_from') }}</label>
                                                <InputNumber v-model="rule.bounds_lower"
                                                             :min="model.pop_rules[index - 1]?.bounds_upper ?? 1"
                                                             :max="model.max_pop" show-buttons class="w-28"/>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.pop_to') }}</label>
                                                <InputNumber v-model="rule.bounds_upper" :max="model.max_pop"
                                                             :min="rule.bounds_lower" show-buttons class="w-28"/>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.pop_multiplier') }}</label>
                                                <InputNumber v-model="rule.price_multiplier" :max-fraction-digits="2"
                                                             show-buttons class="w-28" :min="1"/>
                                            </div>
                                            <Button icon="pi pi-trash" text severity="danger" @click="removePopRule(index)"/>
                                        </div>
                                        <p class="text-xs text-neutral-400">
                                            {{ $t('rental.pop_rule_desc', { from: rule.bounds_lower ?? '…', to: rule.bounds_upper ?? '…', mult: rule.price_multiplier || 1 }) }}
                                        </p>
                                    </div>
                                </div>
                                <p v-else class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.pop_empty') }}</p>
                            </div>

                            <!-- Occupancy rules -->
                            <div class="border-t dark:border-neutral-700 pt-6 flex flex-col gap-4">
                                <div class="flex items-start justify-between gap-4">
                                    <div>
                                        <p class="font-semibold">{{ $t('rental.occ_title') }}</p>
                                        <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{{ $t('rental.occ_hint') }}</p>
                                    </div>
                                    <Button size="small" :label="$t('common.add')" icon="pi pi-plus" outlined @click="addOccupancyRule"/>
                                </div>
                                <div v-if="model.occupancy_rules?.length > 0" class="flex flex-col gap-3">
                                    <div v-for="(rule, index) in model.occupancy_rules" :key="'occ-' + index"
                                         class="flex flex-col gap-2 p-4 rounded-xl border dark:border-neutral-700">
                                        <div class="flex gap-2 flex-wrap items-end">
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.occ_threshold') }}</label>
                                                <InputNumber v-model="rule.occupancy_threshold_percentage" suffix="%" show-buttons class="w-28"/>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.occ_period') }}</label>
                                                <Select v-model="rule.occupancy_collection_time"
                                                        :options="occupancyTimeOptions" option-label="label" option-value="value" class="w-28"/>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.occ_increase') }}</label>
                                                <InputNumber v-model="rule.price_modifier_value"
                                                             :suffix="rule.price_modifier_type === 'percentage' ? '%' : $t('rental.modifier_fixed')" class="w-28"/>
                                            </div>
                                            <div class="flex flex-col gap-1">
                                                <label class="text-xs font-semibold text-neutral-500 uppercase tracking-wide">{{ $t('rental.occ_type') }}</label>
                                                <Select v-model="rule.price_modifier_type" :options="modifierTypes"
                                                        option-label="label" option-value="value" class="w-24"/>
                                            </div>
                                            <Button icon="pi pi-trash" text severity="danger" @click="removeOccupancyRule(index)"/>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <Checkbox v-model="rule.show_price_change" binary/>
                                            <label class="text-sm">{{ $t('rental.occ_show_customer') }}</label>
                                        </div>
                                        <p class="text-xs text-neutral-400">
                                            {{ $t('rental.occ_rule_desc', {
                                                threshold: rule.occupancy_threshold_percentage,
                                                period: occupancyTimeOptions.find(o => o.value === rule.occupancy_collection_time)?.label,
                                                value: rule.price_modifier_value,
                                                unit: rule.price_modifier_type === 'percentage' ? '%' : $t('rental.modifier_fixed')
                                            }) }}
                                        </p>
                                    </div>
                                </div>
                                <p v-else class="text-sm text-gray-400 dark:text-gray-500 italic">{{ $t('rental.occ_empty') }}</p>
                            </div>
                        </div>

                    </div>

                    <!-- Right: live preview (xl+) -->
                    <div class="hidden xl:flex flex-col w-[320px] border-l dark:border-neutral-700 shrink-0 overflow-y-auto bg-neutral-50 dark:bg-neutral-900">
                        <div class="px-4 pt-4 pb-2 border-b dark:border-neutral-700">
                            <p class="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                                <i class="pi pi-eye text-xs"></i> {{ $t('rental.live_preview') }}
                            </p>
                        </div>
                        <div class="p-3 grow overflow-y-auto">
                            <rental-item-card
                                v-if="previewItem"
                                :item="previewItem"
                                :theme="{ color: 'blue' }"
                                roundness="rounded-xl"
                            />
                        </div>
                    </div>

                </div>

                <!-- Footer -->
                <div class="border-t dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 px-6 py-4 flex justify-between items-center shrink-0">
                    <Button :label="$t('common.cancel')" severity="secondary" outlined @click="model = false"/>
                    <div class="flex gap-2">
                        <!-- Mobile: Další / Uložit -->
                        <Button v-if="!isLastSection" class="md:hidden" :label="$t('common.next')" icon="pi pi-arrow-right" icon-pos="right" @click="goToNextSection"/>
                        <Button v-else class="md:hidden" :label="$t('rental.save_item')" icon="pi pi-check" @click="save"/>
                        <!-- Desktop: always Uložit -->
                        <Button class="hidden md:flex" :label="$t('rental.save_item')" icon="pi pi-check" @click="save"/>
                    </div>
                </div>

            </div>
        </template>
    </Dialog>
</template>

<style scoped></style>
